import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLang } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingTicket from "@/components/FloatingTicket";
import Home from "@/pages/Home";
import Tickets from "@/pages/Tickets";
import Merch from "@/pages/Merch";
import Sponsors from "@/pages/Sponsors";
import { Impressum, Datenschutz, Agb, Widerruf, NewsletterBestaetigt, NewsletterAbgemeldet } from "@/pages/Legal";
import Press from "@/pages/Press";
import { EventPage, Anreise } from "@/pages/Events";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentResult from "@/pages/PaymentResult";
import CookieConsent from "@/components/CookieConsent";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Erzwingt die Sprache je nach URL-Praefix (/en = Englisch)
function LangGate({ lang, children }) {
  const { setLang } = useLang();
  useEffect(() => {
    setLang(lang);
  }, [lang, setLang]);
  return children;
}

function NotFound() {
  const { lang } = useLang();
  return (
    <main data-testid="not-found-page" className="pt-40 pb-32 text-center px-5">
      <p className="font-display text-7xl md:text-9xl headline-gold ember-glow">404</p>
      <p className="text-hell-smoke mt-4 mb-8">
        {lang === "en"
          ? "This page does not exist – but the fire still burns."
          : "Diese Seite gibt es nicht – aber das Feuer brennt weiter."}
      </p>
      <Link to="/" className="font-cinzel text-xs tracking-[0.2em] uppercase text-hell-gold border border-hell-gold/40 px-6 py-3 rounded-full hover:bg-hell-gold hover:text-hell-black transition-colors">
        {lang === "en" ? "Back to the show" : "Zurück zur Show"}
      </Link>
    </main>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="min-h-screen bg-hell-black text-hell-bone">
      <ScrollToTop />
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<LangGate lang="de"><Home /></LangGate>} />
        <Route path="/tickets" element={<LangGate lang="de"><Tickets /></LangGate>} />
        <Route path="/merch" element={<LangGate lang="de"><Merch /></LangGate>} />
        <Route path="/sponsoren" element={<LangGate lang="de"><Sponsors /></LangGate>} />
        <Route path="/presse" element={<LangGate lang="de"><Press /></LangGate>} />
        <Route path="/anreise" element={<LangGate lang="de"><Anreise /></LangGate>} />
        <Route path="/krampus-show-2026-11-20" element={<LangGate lang="de"><EventPage day="20" /></LangGate>} />
        <Route path="/krampus-show-2026-11-21" element={<LangGate lang="de"><EventPage day="21" /></LangGate>} />
        <Route path="/impressum" element={<LangGate lang="de"><Impressum /></LangGate>} />
        <Route path="/datenschutz" element={<LangGate lang="de"><Datenschutz /></LangGate>} />
        <Route path="/agb" element={<LangGate lang="de"><Agb /></LangGate>} />
        <Route path="/widerruf" element={<LangGate lang="de"><Widerruf /></LangGate>} />
        <Route path="/newsletter-bestaetigt" element={<LangGate lang="de"><NewsletterBestaetigt /></LangGate>} />
        <Route path="/newsletter-abgemeldet" element={<LangGate lang="de"><NewsletterAbgemeldet /></LangGate>} />
        <Route path="/en" element={<LangGate lang="en"><Home /></LangGate>} />
        <Route path="/en/tickets" element={<LangGate lang="en"><Tickets /></LangGate>} />
        <Route path="/en/merch" element={<LangGate lang="en"><Merch /></LangGate>} />
        <Route path="/en/sponsoren" element={<LangGate lang="en"><Sponsors /></LangGate>} />
        <Route path="/en/presse" element={<LangGate lang="en"><Press /></LangGate>} />
        <Route path="/en/anreise" element={<LangGate lang="en"><Anreise /></LangGate>} />
        <Route path="/en/krampus-show-2026-11-20" element={<LangGate lang="en"><EventPage day="20" /></LangGate>} />
        <Route path="/en/krampus-show-2026-11-21" element={<LangGate lang="en"><EventPage day="21" /></LangGate>} />
        <Route path="/en/impressum" element={<LangGate lang="en"><Impressum /></LangGate>} />
        <Route path="/en/datenschutz" element={<LangGate lang="en"><Datenschutz /></LangGate>} />
        <Route path="/en/agb" element={<LangGate lang="en"><Agb /></LangGate>} />
        <Route path="/en/widerruf" element={<LangGate lang="en"><Widerruf /></LangGate>} />
        <Route path="/en/newsletter-bestaetigt" element={<LangGate lang="en"><NewsletterBestaetigt /></LangGate>} />
        <Route path="/en/newsletter-abgemeldet" element={<LangGate lang="en"><NewsletterAbgemeldet /></LangGate>} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/payment/success" element={<PaymentResult kind="success" />} />
        <Route path="/payment/cancel" element={<PaymentResult kind="cancel" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdmin && <FloatingTicket />}
      {!isAdmin && <CookieConsent />}
      {!isAdmin && <Footer />}
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Shell />
            <Toaster theme="dark" position="bottom-right" />
            <div className="grain-overlay" aria-hidden="true" />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
